import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../../redux/snackbarSlice';
import { getTypeColor, getTypeIcon } from '../../../../utils/nodeTypeMapper';

import { DateTime } from 'luxon';

import {
  useLazyGetDurationAnalysisQuery,
  useLazyGetSeuDurationAnalysisQuery,
} from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';
import EnergyAnalyzerFilterInput from '@/components/forms/energyxplorer/EnergyAnalyzerFilterInput';
import EnergyTreeNodeAutocomplete from '@/features/energyxplorer/components/EnergyTreeNodeAutocomplete';
import DurationAnalysisChart from '@/components/highcharts/powerAnalyser/DurationAnalysisChart';
import TimestampSummaryWarningIcon from '@/features/energyxplorer/components/TimestampsSummaryWarningIcon';
import TimestampSummarySeuWarningIcon from '@/features/energyxplorer/components/TimestampsSummarySeuWarningIcon';

const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  return `${day} ${month}`;
};

const DurationAnalysisTab = ({
  selectedAccount,
  selectedEnergyGroup,
  timeRange,
  setTimestampSummary,
  setTimestampSummarySetBy,
}) => {
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const [
    getDurationAnalysis,
    {
      data: durationAnalysisData,
      isLoading: durationAnalysisLoading,
      isFetching: durationAnalysisFetching,
    },
  ] = useLazyGetDurationAnalysisQuery();
  const [
    getSeuDurationAnalysis,
    {
      data: seuDurationAnalysisData,
      isLoading: seuDurationAnalysisLoading,
      isFetching: seuDurationAnalysisFetching,
    },
  ] = useLazyGetSeuDurationAnalysisQuery();

  const dispatch = useDispatch();

  const [selectedRange, setSelectedRange] = useState([
    DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
    DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
  ]);

  useEffect(() => {
    setSelectedRange([
      DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
      DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
    ]);
  }, [timeRange]);

  const [durationFilters, setDurationFilters] = useState({});

  const [nodeToAnalyse, setNodeToAnalyse] = useState(null);
  const [seuToAnalyse, setSeuToAnalyse] = useState(null);
  const [analysisMode, setAnalysisMode] = useState('node'); // or 'seu'
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [xAxisMode, setXAxisMode] = useState('percentage');
  const [data, setData] = useState([]);

  const fetchDurationAnalysis = async ({ start, end, filters }) => {
    try {
      const response = await getDurationAnalysis({
        accountId: selectedAccount?.AccountId,
        energyGroupId: selectedEnergyGroup?.energyGroupId,
        nodeId: nodeToAnalyse?.energyTreeNodeId,
        start,
        end,
        ...filters,
      }).unwrap();
      setTimestampSummary(response?.TimestampsSummary);
      setTimestampSummarySetBy('Varighedsanalysen');
      return response;
    } catch (error) {
      console.error('Error fetching duration analysis:', error);
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i indhentingen af varighedsanalyse data',
          severity: 'error',
        }),
      );
      setData(null);
      return null;
    }
  };

  const fetchSeuDurationAnalysis = async ({ start, end, filters }) => {
    try {
      const response = await getSeuDurationAnalysis({
        accountId: selectedAccount?.AccountId,
        energyGroupId: selectedEnergyGroup?.energyGroupId,
        seu: seuToAnalyse,
        start,
        end,
        ...filters,
      }).unwrap();
      setTimestampSummary(response?.TimestampsSummary);
      setTimestampSummarySetBy('Varighedsanalysen');
      return response;
    } catch (error) {
      console.error('Error fetching duration analysis:', error);
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i indhentingen af varighedsanalyse data',
          severity: 'error',
        }),
      );
      setData(null);
      return null;
    }
  };

  useEffect(() => {
    const start = selectedRange[0].toMillis();
    const end = selectedRange[1].toMillis();

    if (analysisMode === 'node' && nodeToAnalyse) {
      fetchDurationAnalysis({ start, end, filters: durationFilters }).then(
        (response) => {
          if (response) setData(response);
        },
      );
    }

    if (analysisMode === 'seu' && seuToAnalyse) {
      fetchSeuDurationAnalysis({ start, end, filters: durationFilters }).then(
        (response) => {
          if (response) setData(response);
        },
      );
    }
  }, [
    analysisMode,
    nodeToAnalyse,
    seuToAnalyse,
    selectedRange,
    durationFilters,
  ]);

  useEffect(() => {
    if (!nodeToAnalyse && selectedEnergyGroup?.energyTreeNodes.length > 0) {
      setNodeToAnalyse(selectedEnergyGroup?.energyTreeNodes[0]);
    }
  }, [selectedEnergyGroup]);

  const handleApplyFilters = async (filters) => {
    setDurationFilters(filters);
  };

  // Create a lookup map for quick access
  const nodeLookup = useMemo(() => {
    const map = new Map();

    // First pass: add all nodes
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      map.set(node.energyTreeNodeId, { ...node, children: [] });
    });

    // Second pass: link children to parents
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      if (node.parentId && map.has(node.parentId)) {
        map.get(node.parentId).children.push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const nodeGroupsByType = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      const type = node.options?.type;
      if (type) {
        if (!map.has(type)) map.set(type, []);
        map.get(type).push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const children = useMemo(
    () => nodeLookup.get(nodeToAnalyse?.energyTreeNodeId)?.children || [],
    [nodeToAnalyse, nodeLookup],
  );
  const parent = useMemo(
    () => nodeLookup.get(nodeToAnalyse?.parentId) || null,
    [nodeToAnalyse, nodeLookup],
  );

  const seuInputRef = useRef(null);

  const validSeuOptions = useMemo(() => {
    return Array.from(nodeGroupsByType.entries())
      .filter(([type, nodes]) =>
        nodes.some((node) => node.dataStreams?.length > 0),
      )
      .map(([type]) => type)
      .filter(
        (type) =>
          type !== 'Hovedmåler' && type !== 'Tavle' && type !== 'Undertavle',
      );
  }, [nodeGroupsByType]);

  const effectiveSeuToAnalyse = seuToAnalyse ?? validSeuOptions[0];

  useEffect(() => {
    if (analysisMode === 'seu' && seuInputRef.current) {
      // blur the input immediately after mounting so dropdown doesn't pop open
      seuInputRef.current.blur();
      setSeuToAnalyse(effectiveSeuToAnalyse);
    }
  }, [analysisMode]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid
          item
          md={12}
          lg={filtersOpen ? 9 : 12}
          sx={{ transition: 'flex-basis 0.3s ease' }}
        >
          <Card variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
            <CardContent sx={{ minHeight: 400 }}>
              <Stack
                direction="column"
                color="inherit"
                component={Card}
                elevation={0}
                sx={{
                  transition: 'box-shadow 0.3s ease',
                  height: '100%',
                  background: 'transparent',
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {true && (
                        <>
                          <ListItemText
                            primary={analysisMode === 'node' ? `Node` : `SEU`}
                            primaryTypographyProps={{
                              color: 'text.secondary',
                              variant: 'body2',
                            }}
                            secondary={
                              analysisMode === 'node'
                                ? `${nodeToAnalyse?.name}`
                                : `${seuToAnalyse}`
                            }
                            secondaryTypographyProps={{
                              color: 'text.primary',
                              variant: 'h6',
                              fontWeight: 'bold',
                            }}
                          />
                          {analysisMode === 'node' ? (
                            <TimestampSummaryWarningIcon node={nodeToAnalyse} />
                          ) : (
                            <TimestampSummarySeuWarningIcon
                              seu={seuToAnalyse}
                              energyGroupId={selectedEnergyGroup.energyGroupId}
                            />
                          )}
                          <Tooltip
                            title={t(
                              'duration_analysis.tooltips.duration_analysis_description',
                            )}
                            placement="right"
                          >
                            <InfoRoundedIcon
                              fontSize="small"
                              sx={{
                                color: (theme) =>
                                  theme.palette.mode === 'light'
                                    ? 'lightgray'
                                    : 'gray',
                                ml: 1,
                              }}
                            />
                          </Tooltip>
                        </>
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {t('duration_analysis.in_period')}{' '}
                      {selectedRange[0] && selectedRange[1]
                        ? `${formatDate(selectedRange[0])} - ${formatDate(selectedRange[1])}`
                        : t('duration_analysis.select_period')}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ToggleButtonGroup
                      className="non-printable"
                      value={xAxisMode}
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue) setXAxisMode(newValue);
                      }}
                    >
                      <Tooltip
                        title={t(
                          'duration_analysis.tooltips.shows_axis_in_hours',
                        )}
                        placement="top"
                      >
                        <ToggleButton sx={{ borderRadius: 2 }} value="duration">
                          <AccessTimeRoundedIcon />
                        </ToggleButton>
                      </Tooltip>
                      <Tooltip
                        title={t(
                          'duration_analysis.tooltips.shows_axis_in_percent',
                        )}
                        placement="top"
                      >
                        <ToggleButton
                          sx={{ borderRadius: 2 }}
                          value="percentage"
                        >
                          <PercentRoundedIcon />
                        </ToggleButton>
                      </Tooltip>
                    </ToggleButtonGroup>

                    <Tooltip
                      className="non-printable"
                      title={`${filtersOpen ? t('duration_analysis.tooltips.hide') : t('duration_analysis.tooltips.show')}`}
                      placement="top"
                    >
                      <IconButton
                        onClick={() => setFiltersOpen(!filtersOpen)}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 3,
                          ml: 4,
                        }}
                      >
                        {filtersOpen ? (
                          <KeyboardArrowRightRoundedIcon />
                        ) : (
                          <KeyboardArrowLeftRoundedIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Stack>
              {data ? (
                <Suspense fallback={<div>Loading...</div>}>
                  <DurationAnalysisChart
                    data={data.DurationAnalysisData}
                    loadProfiles={data.LoadProfiles}
                    xAxisMode={xAxisMode}
                    isLoading={
                      durationAnalysisLoading || durationAnalysisFetching
                    }
                  />
                </Suspense>
              ) : (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <AlertTitle>Ingen data tilgængelig</AlertTitle>
                  Det er ikke muligt at vise varighedsanalysen.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {filtersOpen && (
          <Grid className="non-printable" item md={12} lg={3}>
            <Grid item xs>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      fontWeight="bold"
                    >
                      {t('duration_analysis.filtering')}
                    </Typography>
                    <Tooltip
                      title={t('duration_analysis.filtering_description')}
                      placement="right"
                    >
                      <InfoRoundedIcon
                        fontSize="small"
                        sx={{
                          color: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'lightgray'
                              : 'gray',
                          ml: 1,
                        }}
                      />
                    </Tooltip>
                  </Box>
                  <Suspense fallback={<div>Loading...</div>}>
                    <EnergyAnalyzerFilterInput
                      handleApply={handleApplyFilters}
                      setComparisonFilters={setDurationFilters}
                      selectedEnergyGroup={selectedEnergyGroup}
                      startHour={selectedEnergyGroup?.options?.workingDayStart}
                      endHour={selectedEnergyGroup?.options?.workingDayEnd}
                      // viewMode={viewMode}
                    />
                  </Suspense>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs sx={{ mt: 2 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs>
                      {analysisMode === 'node' ? (
                        <>
                          <EnergyTreeNodeAutocomplete
                            nodeOptions={selectedEnergyGroup?.energyTreeNodes}
                            selectedNode={nodeToAnalyse}
                            onChange={(newValue) => {
                              if (newValue) setNodeToAnalyse(newValue);
                            }}
                          />
                          <Box
                            mt={1}
                            display="flex"
                            justifyContent="flex-start"
                          >
                            <Button
                              variant="text"
                              onClick={() => {
                                setAnalysisMode('seu');
                                // setSeuToAnalyse(null);
                              }}
                              sx={{ textTransform: 'none' }}
                              size="small"
                            >
                              {t('buttonLabel.select_seu')}
                            </Button>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Autocomplete
                            options={validSeuOptions} // SEU types
                            getOptionLabel={(option) => option}
                            value={seuToAnalyse}
                            onChange={(e, newValue) =>
                              setSeuToAnalyse(newValue)
                            }
                            disableClearable
                            renderOption={(props, option) => {
                              const isDisabled = [
                                'Hovedmåler',
                                'Tavle',
                                'Undertavle',
                              ].includes(option);
                              return (
                                <li
                                  {...props}
                                  key={option}
                                  style={{
                                    ...props.style,
                                    opacity: isDisabled ? 0.4 : 1,
                                    pointerEvents: isDisabled ? 'none' : 'auto',
                                    cursor: isDisabled
                                      ? 'not-allowed'
                                      : 'pointer',
                                  }}
                                >
                                  {option}
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('buttonLabel.select_seu')}
                                inputRef={seuInputRef}
                                autoFocus={false}
                                variant="outlined"
                                sx={{
                                  '& .MuiInputBase-root': {
                                    borderRadius: '10px',
                                  },
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment: seuToAnalyse ? (
                                    <InputAdornment position="start">
                                      <Avatar
                                        variant="rounded"
                                        sx={{
                                          backgroundColor:
                                            getTypeColor(seuToAnalyse) ||
                                            '#555',
                                          color: '#fff',
                                          fontWeight: 'bold',
                                          borderRadius: 2,
                                        }}
                                      >
                                        <i
                                          className={`fas ${getTypeIcon(seuToAnalyse)}`}
                                        ></i>
                                      </Avatar>
                                    </InputAdornment>
                                  ) : null,
                                }}
                              />
                            )}
                          />

                          <Box
                            mt={1}
                            display="flex"
                            justifyContent="flex-start"
                          >
                            <Button
                              variant="text"
                              onClick={() => {
                                setAnalysisMode('node');
                                // setSeuToAnalyse(null);
                              }}
                              sx={{ textTransform: 'none' }}
                              size="small"
                            >
                              {t('buttonLabel.select_measurement_point')}
                            </Button>
                          </Box>
                        </>
                      )}
                    </Grid>
                    <Grid item>
                      <IconButton
                        onClick={() => parent && setNodeToAnalyse(parent)}
                        disabled={!parent}
                        sx={{
                          p: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 3,
                        }}
                      >
                        <ArrowUpwardRoundedIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                  {analysisMode === 'node' && (
                    <List
                      dense
                      sx={{
                        maxHeight: 425, // or whatever height fits your layout
                        overflowY: 'auto',
                      }}
                    >
                      {children.length > 0 ? (
                        children.map((child) => (
                          <ListItemButton
                            key={child.energyTreeNodeId}
                            onClick={() => setNodeToAnalyse(child)}
                            sx={{ borderRadius: 2 }}
                          >
                            <ListItemIcon>
                              <Avatar
                                variant="round"
                                size="small"
                                sx={{
                                  backgroundColor:
                                    getTypeColor(child.type) || '#555',
                                  color: '#fff',
                                  height: 32,
                                  width: 32,
                                  fontSize: 16,
                                }}
                              >
                                <i
                                  className={`fas ${getTypeIcon(child.type)}`}
                                ></i>
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={child.name}
                              secondary={child.type}
                            />
                          </ListItemButton>
                        ))
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ mt: 1, textAlign: 'center', color: 'gray' }}
                        >
                          {t('duration_analysis.no_further_breakdown')}
                        </Typography>
                      )}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default DurationAnalysisTab;
